import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Input, Switch, Button, message, Space, Tag, Spin, Alert, Grid, Select, Modal } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, CodeOutlined, SnippetsOutlined } from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import cdnService from '../../services/cdnService';
import UserSelect from '../../components/UserSelect';

import { adminPath } from '../../../shared/config/paths';
import { WORKER_TEMPLATES } from '../../../shared/constants/workerTemplates';
import WorkerLimitsTable from '../../../shared/components/cdn/WorkerLimitsTable';

const { TextArea } = Input;
const { useBreakpoint } = Grid;

// Monaco Editor wrapper for Form.Item
const MonacoEditorWrapper = ({ value, onChange }) => {
  return (
    <Editor
      height="400px"
      defaultLanguage="javascript"
      theme="vs-dark"
      value={value}
      onChange={(newValue) => onChange && onChange(newValue)}
      options={{
        minimap: { enabled: false },
        fontSize: 14,
        lineNumbers: 'on',
        scrollBeyondLastLine: false,
        automaticLayout: true,
      }}
    />
  );
};

/**
 * Admin Worker editor. Mirrors the user WorkerDetail page, adapted for admin
 * (adminPath routing; backend operates across all users' workers).
 */
export default function WorkerDetail() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [worker, setWorker] = useState(null);
  const [form] = Form.useForm();

  const isNew = workerId === 'new';

  useEffect(() => {
    if (!isNew) {
      fetchWorker();
    }
  }, [workerId]);

  const fetchWorker = async () => {
    setLoading(true);
    try {
      const res = await cdnService.getWorker(workerId);
      if (res.success) {
        setWorker(res.data);
        form.setFieldsValue({
          name: res.data.name,
          description: res.data.description,
          code: res.data.code,
          enabled: res.data.enabled,
        });
      } else {
        message.error(res.error || '加载Worker失败');
        navigate(adminPath('/cdn/workers'));
      }
    } catch (error) {
      message.error('加载Worker失败：' + error.message);
      navigate(adminPath('/cdn/workers'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const data = {
        name: values.name,
        description: values.description,
        code: values.code,
        enabled: values.enabled ?? true,
      };

      if (isNew) {
        data.user_id = values.user_id;
      } else {
        data.id = parseInt(workerId);
      }

      const res = await cdnService.saveWorker(data);

      if (res.success) {
        message.success(isNew ? '创建成功' : '保存成功');
        if (isNew && res.data?.id) {
          navigate(adminPath(`/cdn/workers/${res.data.id}`), { replace: true });
        } else if (isNew) {
          navigate(adminPath('/cdn/workers'));
        } else {
          fetchWorker(); // Reload to get updated version
        }
      } else {
        message.error(res.error || '保存失败');
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('请检查表单输入');
      } else {
        message.error('保存失败：' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(adminPath('/cdn/workers'));
  };

  const applyTemplate = (tplKey) => {
    const tpl = WORKER_TEMPLATES.find((t) => t.key === tplKey);
    if (!tpl) return;
    const existing = form.getFieldValue('code');
    const doApply = () => {
      form.setFieldsValue({ code: tpl.code });
      if (!form.getFieldValue('name')) {
        form.setFieldsValue({ name: tpl.label });
      }
      message.success(`已载入模板：${tpl.label}`);
    };
    if (existing && existing.trim()) {
      Modal.confirm({
        title: '覆盖当前代码？',
        content: `载入模板「${tpl.label}」将替换编辑器中的现有代码，是否继续？`,
        okText: '覆盖',
        cancelText: '取消',
        onOk: doApply,
      });
    } else {
      doApply();
    }
  };

  return (
    <div style={{ padding: isMobile ? '12px 0' : 0 }}>
      {/* Header */}
      <div style={{
        marginBottom: isMobile ? 16 : 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12
      }}>
        <Space size={isMobile ? 8 : 16}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '返回' : '返回列表'}
          </Button>
          <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 20 }}>
            {isNew ? '创建 Worker' : (worker?.name || 'Worker 详情')}
          </h2>
          {!isNew && worker && (
            <Tag color={worker.enabled ? 'green' : 'default'}>
              {worker.enabled ? '已启用' : '已禁用'}
            </Tag>
          )}
        </Space>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSubmit}
          loading={saving}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? '保存' : '保存更改'}
        </Button>
      </div>

      {/* Worker Info */}
      {!isNew && worker && (
        <Alert
          message={
            <Space size={isMobile ? 8 : 16} wrap>
              <span>版本: v{worker.version}</span>
              <span>用户ID: {worker.user_id}</span>
              <span>更新时间: {worker.updated_at}</span>
              {worker.bytecode_size && (
                <span>字节码大小: {(worker.bytecode_size / 1024).toFixed(1)} KB</span>
              )}
            </Space>
          }
          type="info"
          style={{ marginBottom: isMobile ? 16 : 24 }}
        />
      )}

      {/* Form */}
      <Spin spinning={loading}>
      <Card size={isMobile ? 'small' : 'default'}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ enabled: true }}
          size={isMobile ? 'middle' : 'default'}
        >
          {isNew && (
            <Form.Item label="所属用户" name="user_id" rules={[{ required: true, message: '请选择 Worker 所属用户' }]}>
              <UserSelect allowClear={false} />
            </Form.Item>
          )}
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请输入Worker名称' }]}
          >
            <Input placeholder="Worker名称" />
          </Form.Item>

          <Form.Item label="描述" name="description">
            <TextArea rows={isMobile ? 2 : 3} placeholder="Worker描述（可选）" />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <SnippetsOutlined />
                从模板开始（可选）
              </Space>
            }
            tooltip="选择一个示例模板填充下方编辑器，可在此基础上修改"
          >
            <Select
              placeholder="选择一个示例模板…"
              allowClear
              onChange={(val) => val && applyTemplate(val)}
              value={undefined}
              options={WORKER_TEMPLATES.map((t) => ({
                value: t.key,
                label: `${t.label} — ${t.description}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <CodeOutlined />
                JavaScript 代码
              </Space>
            }
            name="code"
            rules={[{ required: true, message: '请输入JavaScript代码' }]}
          >
            {isMobile ? (
              <TextArea
                rows={15}
                placeholder="// 输入 JavaScript 代码"
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            ) : (
              <MonacoEditorWrapper />
            )}
          </Form.Item>

          <Form.Item
            label="启用"
            name="enabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>

        {/* Supported Features */}
        <div style={{
          marginTop: isMobile ? 16 : 24,
          padding: isMobile ? 12 : 16,
          background: '#f0f5ff',
          borderRadius: 4,
          border: '1px solid #d6e4ff'
        }}>
          <p style={{ margin: 0, fontSize: isMobile ? 12 : 13, color: '#333', marginBottom: 8 }}>
            <strong>支持的 API：</strong>
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: isMobile ? 11 : 12, color: '#666' }}>
            <li><strong>Async/Await</strong> - 原生异步编程支持</li>
            <li><strong>Fetch API</strong> - <code>await fetch(url)</code>，支持链式请求</li>
            <li><strong>Crypto</strong> - <code>crypto.md5()</code>, <code>crypto.sha256()</code>, <code>crypto.base64Encode()</code>, <code>crypto.randomUUID()</code></li>
            <li><strong>Console</strong> - <code>console.log()</code> 调试输出</li>
            <li><strong>Response</strong> - <code>resp.json()</code>, <code>resp.text()</code>, <code>resp.status</code>, <code>resp.ok</code></li>
          </ul>
        </div>

        {/* Resource Limits */}
        <div style={{
          marginTop: isMobile ? 12 : 16,
          padding: isMobile ? 12 : 16,
          background: '#fff7e6',
          borderRadius: 4,
          border: '1px solid #ffd591'
        }}>
          <p style={{ margin: 0, fontSize: isMobile ? 12 : 13, color: '#333', marginBottom: 8 }}>
            <strong>资源限制：</strong>
          </p>
          {worker?.limits ? <WorkerLimitsTable limits={worker.limits} /> : <span style={{ fontSize: 12, color: '#666' }}>保存后显示</span>}
        </div>

        {/* Code Examples */}
        <div style={{
          marginTop: isMobile ? 12 : 16,
          padding: isMobile ? 12 : 16,
          background: '#f6ffed',
          borderRadius: 4,
          border: '1px solid #b7eb8f'
        }}>
          <p style={{ margin: 0, fontSize: isMobile ? 12 : 13, color: '#333', marginBottom: 8 }}>
            <strong>编程模型（对齐 Cloudflare Workers）：</strong>导出 <code>export default</code> 对象，实现 <code>fetch(request, env, ctx)</code>。
            返回 <code>new Response(...)</code> 短路，或 <code>{'{ action: "continue" }'}</code> 交由回源。
          </p>
          <pre style={{
            margin: 0,
            fontSize: isMobile ? 10 : 11,
            background: '#fafafa',
            padding: 8,
            borderRadius: 4,
            overflow: 'auto'
          }}>
{`export default {
  async fetch(request, env, ctx) {
    // 发起外部请求（受 SSRF 白名单约束）
    const resp = await fetch('https://api.example.com/data');
    const data = await resp.json();

    // 返回响应
    return new Response(JSON.stringify({ message: 'Hello!', data }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }
};`}
          </pre>
        </div>
      </Card>
      </Spin>
    </div>
  );
}
